/**
 * Shop Integrations Hooks
 * Shop integrations için React Query hooks'ları
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import shopIntegrationsService, { 
  type CreateShopIntegrationData, 
  type UpdateShopIntegrationData,
  type ShopIntegration,
  type SupportedPlatform 
} from '@/services/shop-integrations.service';

/**
 * Shop integrations listesini getiren hook
 */
export const useShopIntegrations = (organizationId: string) => {
  return useQuery({
    queryKey: ['shop-integrations', organizationId],
    queryFn: () => shopIntegrationsService.getShopIntegrations(organizationId),
    staleTime: 5 * 60 * 1000, // 5 dakika
    enabled: !!organizationId, // organizationId varsa çalıştır
  });
};

/**
 * Desteklenen platformları getiren hook
 */
export const useSupportedPlatforms = () => {
  return useQuery({
    queryKey: ['shop-platforms'],
    queryFn: () => shopIntegrationsService.getSupportedPlatforms(),
    staleTime: 30 * 60 * 1000, // 30 dakika - platforms rarely change
  });
};

/**
 * Yeni shop integration oluşturan hook
 */
export const useCreateShopIntegration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ organizationId, data }: { organizationId: string; data: CreateShopIntegrationData }) => 
      shopIntegrationsService.createShopIntegration(organizationId, data),
    onSuccess: (_, { organizationId }) => {
      // Shop integrations listesini yenile
      queryClient.invalidateQueries({ queryKey: ['shop-integrations', organizationId] });
    },
    onError: (error) => {
      console.error('Shop integration oluşturma hatası:', error);
    }
  });
};

/**
 * Shop integration güncelleyen hook
 */
export const useUpdateShopIntegration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShopIntegrationData }) => 
      shopIntegrationsService.updateShopIntegration(id, data),
    onSuccess: () => {
      // Shop integrations listesini yenile
      queryClient.invalidateQueries({ queryKey: ['shop-integrations'] });
    },
    onError: (error) => {
      console.error('Shop integration güncelleme hatası:', error);
    }
  });
};

/**
 * Shop integration silen hook
 */
export const useDeleteShopIntegration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => shopIntegrationsService.deleteShopIntegration(id),
    onSuccess: () => {
      // Shop integrations listesini yenile
      queryClient.invalidateQueries({ queryKey: ['shop-integrations'] });
    },
    onError: (error) => {
      console.error('Shop integration silme hatası:', error);
    }
  });
};

/**
 * Shop integration bağlantısını test eden hook
 */
export const useTestShopIntegration = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => shopIntegrationsService.testShopIntegration(id),
    onSuccess: () => {
      // Test sonrası shop integrations listesini yenile (test sonuçları güncellenebilir)
      queryClient.invalidateQueries({ queryKey: ['shop-integrations'] });
    },
    onError: (error) => {
      console.error('Shop integration test hatası:', error);
    }
  });
};